import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {
  Subject,
  switchMap,
  takeUntil,
  of,
  tap,
  map,
  take,
  distinctUntilChanged,
  combineLatest,
  debounceTime,
  pairwise,
  startWith,
  merge
} from 'rxjs';
import {Title} from '@angular/platform-browser';
import {DepreciationMethod, ProrateConvention} from '../../../DataModels/depreciation-models';
import {FixedAssetModel} from '../../../DataModels/fixed-asset-model';
import {FixedAssetConfigModel} from '../../../DataModels/fixed-asset-config-model';
import {FixedAssetsPostingService} from '../../../Services/fixed-assets-posting.service';
import {MacrsService} from '../../../Services/macrs.service';
import {DataService} from '../../../Services/data.service';
import {ResponseModel} from '../../../DataModels/response-model';
import {EmailModel} from '../../../DataModels/email-model';
import {ConfirmDialogService} from '../../../Services/confirm-dialog.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {FixedAssetsDataSourceService} from '../../../Services/fixed-assets-data-source.service';
import {FixedAssetsConfigService} from '../../../Services/fixed-assets-config.service';

@Component({
  selector: 'app-fixed-asset-form',
  templateUrl: './fixed-asset-form.component.html',
  styleUrls: ['./fixed-asset-form.component.css'],
  standalone: false
})
export class FixedAssetFormComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  id: string | null = null;
  methods = Object.values(DepreciationMethod);
  asset: FixedAssetModel;
  // Lookups
  companies: any[] = [];
  locations: any[] = [];
  entities: any[] = [];
  // MACRS values are provided by the API per schedule row; no local maps
  // Standard form header button config
  buttonConfig: any = {
    save: true,
    close: true,
    reset: false,
    delete: true,
    saveNew: true,
    saveClose: true,
    print: true,
    email: true,
  };
  // Throttle flags to control when actions become enabled
  assetReady = false;
  configReady = false;
  // Posting eligibility (computed reactively; avoids early getter evaluation)
  canPostDepBool = false;
  hasUnpostedDepBool = false;
  // MACRS categories mapped to useful life in months
  macrsOptions: { value: string; months: number }[] = [
    {value: '3-year', months: 36},
    {value: '5-year', months: 60},
    {value: '7-year', months: 84},
    {value: '10-year', months: 120},
    {value: '15-year', months: 180},
    {value: '20-year', months: 240},
    {value: '27.5-year', months: 330}, // residential rental real property
    {value: '39-year', months: 468},   // nonresidential real property
  ];

  // View model for the shared account select modal
  vm: any = {
    accountFilter: {search_string: '', type_name: ''},
    startAccount: 0,
    tempAccountDetailId: '', // holds the target form control name when opening the modal
    account: [] as any[],
  };

  private accountLookup = new Map<string, { id: string; number: string; name: string }>();
  // Prevents flashing raw ids while accounts are loading
  accountsLoaded = false;
  private destroy$ = new Subject<void>();
  // Guard to prevent preview calls during initial load/patch where schedule already exists
  private allowPreviewChanges = false;
  // Iframe host for single-asset report (print/email)
  @ViewChild('assetReportFrame') assetReportFrame?: ElementRef<HTMLIFrameElement>;
  // Local printable area container
  @ViewChild('faPrintArea') faPrintArea?: ElementRef<HTMLElement>;
  assetReportEmail: EmailModel = new EmailModel();
  private reportMsgHandler = (ev: MessageEvent) => {
    this.onReportMessage(ev);
  };
  // Busy indicator while building/printing/emailing the embedded report
  reportBusy = false;
  // Calculated on init/load: Depreciation to Date (as of today) from schedule
  depToDateToday: number = 0;
  // Calculated: MACRS Depreciation to Date (as of today) from API-provided schedule rows
  macrsDepToDateToday: number = 0;
  // Internal flag to avoid re-applying account defaults after user starts editing
  private didApplyAccountDefaults = false;

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private titleService: Title,
    private ds: FixedAssetsDataSourceService,
    private posting: FixedAssetsPostingService,
    private macrs: MacrsService,
    private tableDataService: DataService,
    private confirm: ConfirmDialogService,
    private notification: BsToastService,
    private faConfigApi: FixedAssetsConfigService,
  ) {
    this.form = this.fb.group({
      id: new UntypedFormControl(''),
      name: new UntypedFormControl('', [Validators.required]),
      assetTag: new UntypedFormControl(''),
      category: new UntypedFormControl(''), // will be used as MACRS Category label
      description: new UntypedFormControl(''),
      // bsDatepicker expects Date objects; use null default and map to/from ISO strings
      acquisitionDate: new UntypedFormControl(null, {validators: [Validators.required], updateOn: 'blur'}),
      inServiceDate: new UntypedFormControl(null, {updateOn: 'blur'}),
      acquisitionCost: new UntypedFormControl(0, {validators: [Validators.required, Validators.min(0)], updateOn: 'blur'}),
      salvageValue: new UntypedFormControl(0, {validators: [Validators.min(0)], updateOn: 'blur'}),
      usefulLifeMonths: new UntypedFormControl(60, {validators: [Validators.required, Validators.min(1)], updateOn: 'blur'}),
      depreciationMethod: new UntypedFormControl(DepreciationMethod.None, [Validators.required]),
      totalUnits: new UntypedFormControl(0, {updateOn: 'blur'}),
      // Required contextual fields
      companyId: new UntypedFormControl('', [Validators.required]),
      locationId: new UntypedFormControl('', [Validators.required]),
      entityId: new UntypedFormControl('', [Validators.required]),
      assetAccountId: new UntypedFormControl(''),
      accumulatedDepreciationAccountId: new UntypedFormControl(''),
      depreciationExpenseAccountId: new UntypedFormControl(''),
      gainOnDisposalAccountId: new UntypedFormControl(''),
      lossOnDisposalAccountId: new UntypedFormControl(''),
      proceedsSuspenseAccountId: new UntypedFormControl('', [Validators.required]),
      active: new UntypedFormControl(true),
      // Disposal fields (local to form)
      disposalDate: new UntypedFormControl(null),
      disposalAmount: new UntypedFormControl(null),
      disposalExpenses: new UntypedFormControl(null),
    });
  }

  // Sanitize and apply decimal input into reactive form controls (keeps numeric values)
  onDecimalInput(ev: Event, controlName: string): void {
    const input = ev.target as HTMLInputElement;
    let val = input.value ?? '';
    // Allow digits and one dot; strip other characters
    val = val.replace(/[^0-9.]/g, '');
    const firstDot = val.indexOf('.');
    if (firstDot !== -1) {
      // remove any additional dots
      val = val.substring(0, firstDot + 1) + val.substring(firstDot + 1).replace(/\./g, '');
    }
    input.value = val;
    const num = val === '' ? null : Number(val);
    const ctrl = this.form?.get(controlName);
    if (ctrl) {
      ctrl.setValue(Number.isFinite(num as number) ? num : null, {emitEvent: true});
    }
  }

  // Ensure control displays two decimals after blur (value remains numeric)
  formatDecimalControl(controlName: string): void {
    const ctrl = this.form?.get(controlName);
    if (!ctrl) {
      return;
    }
    const v = ctrl.value;
    if (v === null || v === undefined || v === '') {
      return;
    }
    const num = Number(v);
    if (Number.isFinite(num)) {
      // Set numeric value rounded to 2 decimals
      const rounded = Math.round(num * 100) / 100;
      ctrl.setValue(rounded, {emitEvent: true});
    }
  }

  ngOnInit(): void {
    this.titleService.setTitle($localize`:Fixed Asset@@fixedAsset:Fixed Asset`);
    // Initialize live recalculation subscriptions for MACRS and Method selections
    this.initRecalcSubscriptions();
    // React to route param changes so component reuse does not skip load
    this.route.paramMap
      .pipe(
        takeUntil(this.destroy$),
        switchMap(pm => {
          this.id = pm.get('id');
          return this.loadAsset(this.id);
        })
      )
      .subscribe();
    // Keep useful life in sync when user changes MACRS Category
    this.form.get('category')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val: string) => {
      const match = this.macrsOptions.find(o => o.value === val);
      if (match) {
        const ctl = this.form.get('usefulLifeMonths');
        if (ctl && ctl.value !== match.months) {
          ctl.setValue(match.months);
        }
      }
    });
    // Load config for account fallbacks (ensure recompute runs only after completion) from API
    this.faConfigApi.get()
      .pipe(take(1))
      .subscribe({
        next: (cfg) => {
          // Map API DTO (strings) to FixedAssetConfigModel (enum types)
          this.config = {
            ...(cfg as any),
            defaultDepreciationMethod: (cfg as any)?.defaultDepreciationMethod as any as DepreciationMethod,
            prorateConvention: (cfg as any)?.prorateConvention as any as ProrateConvention,
          } as unknown as FixedAssetConfigModel;
          this.configReady = true;
        },
        error: () => {
          // keep defaults; eligibility will remain false
        },
        complete: () => {
          // For new assets, attempt to prefill account fields from config defaults once
          this.applyAccountDefaultsIfNew();
          this.recomputeWhenReady();
        }
      });

    // Load accounts for the account modal and for rendering selected labels
    this.tableDataService
      .getApiListData('account', '?state=active&pageNumber=0&pageSize=999999')
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: ResponseModel) => {
        const accounts = (result as any)?.Data ?? result;
        this.vm.account = accounts;
        this.accountLookup = new Map(
          (accounts as any[]).map((a: any) => [String(a.id), {id: String(a.id), number: a.number, name: a.name}])
        );
        // mark lookup ready to avoid raw id flash
        this.accountsLoaded = true;
      });

    // Load lookups for Company, Location, Entity
    try {
      this.tableDataService.getData('company?state=active&pageNumber=0&pageSize=999999')
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          (this as any).companies = (res as any)?.Data ?? res ?? [];
        });
      this.tableDataService.getData('location?state=active&pageNumber=0&pageSize=999999')
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          (this as any).locations = (res as any)?.Data ?? res ?? [];
        });
      this.tableDataService.getData('entity?state=active&pageNumber=0&pageSize=999999')
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          (this as any).entities = (res as any)?.Data ?? res ?? [];
        });
    } catch {
    }

    // Listen for report iframe messages (HTML for email)
    try {
      window.addEventListener('message', this.reportMsgHandler as any);
    } catch {
    }

    // Recompute eligibility when relevant form fields change
    this.form.get('depreciationMethod')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.recomputeWhenReady());
    this.form.get('depreciationExpenseAccountId')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.recomputeWhenReady());
    this.form.get('accumulatedDepreciationAccountId')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.recomputeWhenReady());

    // Set up live preview of schedule from API when key calculator fields are complete/changed
    this.setupPreviewScheduleSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    try {
      window.removeEventListener('message', this.reportMsgHandler as any);
    } catch {
    }
  }

  // No local MACRS computations; API is source of truth

  // --- Single-asset report integration (print/email) ---
  onPrintReport(): void {
    const iframe = this.assetReportFrame?.nativeElement;
    const area = this.faPrintArea?.nativeElement;
    if (!iframe || !area) {
      return;
    }
    const title = $localize`:Fixed Asset Depreciation Schedule@@faDepSched:Fixed Asset Depreciation Schedule`;
    const shell = this.buildReportHtmlShell(title, area.outerHTML);
    this.reportBusy = true;
    try {
      const doc = iframe.contentDocument || iframe.ownerDocument;
      if (!doc) {
        throw new Error('no iframe document');
      }
      doc.open();
      doc.write(shell);
      doc.close();
      // Wait a tick for layout/styles, then print
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } finally {
          this.reportBusy = false;
        }
      }, 50);
    } catch {
      this.reportBusy = false;
    }
  }

  onEmailReport(): void {
    const area = this.faPrintArea?.nativeElement;
    if (!area) {
      return;
    }
    const reportName = $localize`:Fixed Asset Depreciation Schedule@@faDepSched:Fixed Asset Depreciation Schedule`;
    const html = this.buildReportHtmlShell(reportName, area.outerHTML);
    const emailDocObj = new EmailModel();
    emailDocObj.print_orientation = '2';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = '';
    emailDocObj.toEmail = '';
    emailDocObj.body = $localize`:EmailBody@@emailBody:Please see the attached ${reportName}.` + '<br><br><br>Best regards,<br><br>AcctXErp<br><br>';
    emailDocObj.Subject = 'AcctXErp ' + reportName + ' ' + this.form.value.name + ' ' + new Date().toISOString();
    emailDocObj.pdfHtml = html;
    const now = new Date();
    const yyyy = String(now.getFullYear());
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    emailDocObj.FileName = `Fixed_Asset_Depreciation_${yyyy}${mm}${dd}.pdf`;
    emailDocObj.activity_message = reportName;
    this.assetReportEmail = emailDocObj;
    // open modal
    const element = document.getElementById('sendEmailModal') as HTMLElement;
    if (element) {
      try {
        const modal = new (window as any).bootstrap.Modal(element);
        modal.show();
      } catch {
        try {
          (element as any)?.show?.();
        } catch {
        }
      }
    }
  }

  private onReportMessage(ev: MessageEvent): void {
    try {
      if (ev.origin !== window.location.origin) {
        return;
      }
    } catch {
    }
    const data: any = ev.data || {};
    if (!data || typeof data !== 'object') {
      return;
    }
    if (data.type === 'reportHtml') {
      // Clear busy state once HTML is received for email
      this.reportBusy = false;
      const reportName = String(data.title || 'Asset Depreciation');
      const emailDocObj = new EmailModel();
      emailDocObj.print_orientation = '1';
      emailDocObj.convert_attachment_to = 'pdf';
      emailDocObj.fromEmail = '';
      emailDocObj.fromName = 'AcctXErp';
      emailDocObj.header_id = '';
      emailDocObj.toEmail = '';
      emailDocObj.body = $localize`:EmailBody@@emailBody:Please see the attached ${reportName}.` + '<br><br><br>Best regards,<br><br>AcctXErp<br><br>';
      emailDocObj.Subject = 'AcctXErp ' + reportName;
      emailDocObj.pdfHtml = String(data.html || '');
      emailDocObj.FileName = String(data.fileName || 'Asset_Depreciation.pdf');
      emailDocObj.activity_message = reportName;
      this.assetReportEmail = emailDocObj;
      // open modal
      const element = document.getElementById('sendEmailModal') as HTMLElement;
      if (element) {
        try {
          const modal = new (window as any).bootstrap.Modal(element);
          modal.show();
        } catch {
          // fallback to Bootstrap import path if available globally
          try {
            (element as any)?.show?.();
          } catch {
          }
        }
      }
    }
  }

  // Removed local MACRS map helpers; API schedule carries MACRS amounts

  onSave(): void {
    // Save and stay on page
    this.persist(false);
  }

  // Hook reactive listeners after form is created (in ngOnInit) to recalc on dropdown changes
  private initRecalcSubscriptions(): void {
    // Subscribe to MACRS category changes: update useful life and rebuild full schedule
    this.form.get('category')?.valueChanges
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((val) => {
        try {
          this.onMacrsCategoryChanged(String(val || ''));
        } catch (e) {
          try {
            console.warn('[FA Form] onMacrsCategoryChanged failed', e);
          } catch {
          }
        }
      });

    // Subscribe to Method changes: trigger a single forced preview on any change to a concrete method
    this.form.get('depreciationMethod')?.valueChanges
      .pipe(startWith(this.form.get('depreciationMethod')?.value), pairwise(), takeUntil(this.destroy$))
      .subscribe(([prev, next]) => {
        try {
          const prevMethod = String(prev || '');
          const newMethod = String(next || '');
          if (newMethod === prevMethod) {
            return; // no-op
          }
          // Always update asset method
          this.onMethodChanged(newMethod);
          // Trigger preview once when switching to a concrete method (non-None)
          // Guard against initial load: do not trigger while initial patching is in progress
          if (!this.allowPreviewChanges) {
            return;
          }
          if (newMethod && newMethod !== 'None') {
            setTimeout(() => {
              try {
                console.log('[FA Form] forced method-change preview', {prevMethod, newMethod});
              } catch {
              }
              this.requestPreviewSchedule(true);
            }, 0);
          }
        } catch (e) {
          try {
            console.warn('[FA Form] depreciationMethod pairwise handler failed', e);
          } catch {
          }
        }
      });
  }

  // Ensure cleanup existing subscription pattern remains (ngOnDestroy exists elsewhere in file)

  // Handle MACRS category change: set useful life months and request API preview
  private onMacrsCategoryChanged(category: string): void {
    if (!this.asset) {
      return;
    }
    const months = this.macrs.getMacrsMonths(category) ?? (this.macrsOptions.find(o => o.value === category)?.months || null);
    if (!months || months <= 0) {
      return; // nothing to do
    }
    // update form useful life months
    this.form.get('usefulLifeMonths')?.setValue(months, {emitEvent: false});
    // set on asset too for downstream calcs
    this.asset.usefulLifeMonths = months;
    this.asset.category = category;
    // Do not call preview directly here; the debounced valueChanges pipeline handles it.
  }

  // Handle Method change: rely on API preview only
  private onMethodChanged(method: string): void {
    if (!this.asset) {
      return;
    }
    this.asset.depreciationMethod = method as any;
    // Do not call preview directly here; method changes are handled by the pairwise handler/preview pipeline.
  }

  onSaveClose(): void {
    // Save and navigate back to list
    this.persist(true);
  }

  // Save current asset, then refresh the form to add a new one
  onSaveNew(): void {
    const formVal = this.form.value as any;
    // Ensure correct id handling: existing id for existing records; zero-Guid for NEW
    const zeroGuid = '00000000-0000-0000-0000-000000000000';
    const outgoingId = formVal?.id ? formVal.id : zeroGuid;
    // Resolve display names for mock persistence (temporary; API will later store only Ids)
    const companyName = this.findNameById(this.companies, formVal.companyId);
    const locationName = this.findNameById(this.locations, formVal.locationId);
    const entityName = this.findNameById(this.entities, formVal.entityId);

    const model = {
      ...this.asset,
      ...formVal,
      id: outgoingId,
      companyName,
      locationName,
      entityName,
      acquisitionDate: this.toIsoDate(formVal.acquisitionDate) || this.asset?.acquisitionDate,
      inServiceDate: this.toIsoDate(formVal.inServiceDate) || undefined,
    } as FixedAssetModel;

    const obs = model.id ? this.ds.update(model) : this.ds.create(model);
    obs.subscribe(() => {
      // After saving, navigate to a fresh new asset form
      this.router.navigate(['/fixed-assets/fixed-asset-form', 'new']);
    });
  }

  // confirmDelete() {
  //   if (this.vm.list.filter(x => x.selected).length === 0) {
  //     this.notification.showWarningToast('No records selected to delete!');
  //     return;
  //   }
  //   const messages: string[] = [
  //     $localize`:Are you sure you want to Delete? @@areyousureyouwanttodelete:Are you sure you want to Delete?`,
  //     'Selected Count: ' + this.vm.list.filter(x => x.selected).length,
  //   ];
  //   this.confirm.confirmThis(
  //     messages,
  //     () => {
  //       // this.deleteSelected();
  //     },
  //     () => {
  //       this.notification.showSuccessToast($localize`:Delete Canceled @@deleteCanceled:Delete Canceled`);
  //     }
  //   );
  // }

  // Delete with common confirm dialog
  onDelete(): void {
    const id = this.form?.value?.id || this.asset?.id;
    if (!id) {
      return;
    }

    const messages: string[] = [
      $localize`:Are you sure you want to Delete? @@areyousureyouwanttodelete:Are you sure you want to Delete?`,
      `${$localize`:Asset@@asset:Asset`}: ${this.form?.value?.name || this.asset?.name || id}`,
    ];

    this.confirm.confirmThis(
      messages,
      () => {
        this.ds.delete(String(id)).subscribe(() => {
          this.notification.showSuccessToast($localize`:Deleted@@deleted:Deleted`);
          this.router.navigate(['/fixed-assets/fixed-asset-list']);
        });
      },
      () => {
        this.notification.showSuccessToast($localize`:Delete Canceled@@deleteCanceled:Delete Canceled`);
      }
    );
  }

  private persist(navigateAfter: boolean): void {
    const formVal = this.form.value as any;
    // Ensure correct id handling on save: keep existing id; use zero-Guid for NEW
    const zeroGuid = '00000000-0000-0000-0000-000000000000';
    const outgoingId = formVal?.id ? formVal.id : zeroGuid;
    // Resolve display names for mock persistence (temporary; API will later store only Ids)
    const companyName = this.findNameById(this.companies, formVal.companyId);
    const locationName = this.findNameById(this.locations, formVal.locationId);
    const entityName = this.findNameById(this.entities, formVal.entityId);

    // Clone schedule to ensure immutability; keep API-provided MACRS/book fields as-is
    const sched = Array.isArray(this.asset?.schedule) ? [...(this.asset!.schedule as any[])] : [];
    if (sched.length) {
      for (let i = 0; i < sched.length; i++) {
        const row = {...sched[i]};
        // Ensure period number exists for UI and storage
        row.periodNumber = Number(row.periodNumber) > 0 ? Number(row.periodNumber) : (i + 1);
        sched[i] = row;
      }
    }

    const model = {
      ...this.asset,
      ...formVal,
      id: outgoingId,
      // updated schedule with MACRS values/period numbers
      schedule: sched,
      companyName,
      locationName,
      entityName,
      // Map disposal form fields into nested disposal object for persistence (mock)
      disposal: {
        ...(this.asset?.disposal || {disposed: false}),
        disposalDate: this.toIsoDate(formVal.disposalDate) || this.asset?.disposal?.disposalDate,
        proceeds: formVal.disposalAmount != null && formVal.disposalAmount !== '' ? Number(formVal.disposalAmount) : this.asset?.disposal?.proceeds,
        expenses: formVal.disposalExpenses != null && formVal.disposalExpenses !== '' ? Number(formVal.disposalExpenses) : this.asset?.disposal?.expenses,
        entryDate: this.asset?.disposal?.entryDate,
        disposed: !!(this.asset?.disposal?.disposalDate || formVal.disposalDate)
      },
      // Convert Date objects back to ISO strings for storage/service
      acquisitionDate: this.toIsoDate(formVal.acquisitionDate) || this.asset?.acquisitionDate,
      inServiceDate: this.toIsoDate(formVal.inServiceDate) || undefined,
    } as FixedAssetModel;

    const obs = model.id ? this.ds.update(model) : this.ds.create(model);
    obs.subscribe((saved: any) => {
      // API may return only { id }; re-fetch full model by id then patch
      const id = String((saved && saved.id) ? saved.id : (model.id || ''));
      if (id) {
        this.ds.getById(id).pipe(take(1)).subscribe(a => {
          if (a) {
            this.asset = a;
            const {acquisitionDate, inServiceDate, ...rest} = a as any;
            // Prevent any preview triggers while patching fresh values after save
            this.allowPreviewChanges = false;
            this.form.patchValue({
              ...rest,
              acquisitionDate: this.toNgbDate(a.acquisitionDate),
              inServiceDate: this.toNgbDate(a.inServiceDate || a.acquisitionDate)
            }, {emitEvent: false});
            // Populate disposal inputs from API: picker uses DisposalDate; amounts from proceeds/expenses
            const disp: any = (a as any).disposal || {};
            this.form.patchValue({
              disposalDate: this.toNgbDate(disp?.disposalDate) ?? null,
              disposalAmount: disp?.proceeds ?? null,
              disposalExpenses: disp?.expenses ?? null,
            }, {emitEvent: false});
            this.updateDisposalControlState();
            // Mark form clean and re-enable preview for subsequent user edits only
            this.form.markAsPristine();
            this.allowPreviewChanges = true;
          }
          if (navigateAfter) {
            this.router.navigate(['/fixed-assets/fixed-asset-list']);
          }
        });
      } else {
        // If no id available, still navigate if requested
        if (navigateAfter) {
          this.router.navigate(['/fixed-assets/fixed-asset-list']);
        }
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/fixed-assets/fixed-asset-list']);
  }

  // Fixed Asset Config cached for posting
  config: FixedAssetConfigModel | undefined;

  // Determine if there are any unposted months through current month (sets hasUnpostedDepBool)
  private computeHasUnpostedDep(): void {
    if (!this.asset) {
      this.hasUnpostedDepBool = false;
      return;
    }
    const inSvcStr = this.asset.inServiceDate || this.asset.acquisitionDate;
    if (!inSvcStr) {
      this.hasUnpostedDepBool = false;
      return;
    }
    const inSvc = new Date(inSvcStr);
    const today = new Date();
    const monthsAll: string[] = this.monthsBetween(inSvc, today);
    const already = new Set(this.asset.postedDepMonths || []);
    // If config has a prorate convention, only count months in fully completed periods
    const conv: ProrateConvention = (this.config as any)?.prorateConvention || ProrateConvention.Monthly;
    const candidates = monthsAll.filter(k => !already.has(k));
    const completed = this.filterToCompletedPeriods(candidates, today, conv);
    this.hasUnpostedDepBool = completed.length > 0;
  }

  private monthsBetween(start: Date, end: Date): string[] {
    const ym = (d: Date) => `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`;
    const s = new Date(start.getFullYear(), start.getMonth(), 1);
    const e = new Date(end.getFullYear(), end.getMonth(), 1);
    const out: string[] = [];
    for (let d = new Date(s); d <= e; d.setMonth(d.getMonth() + 1)) {
      out.push(ym(d));
    }
    return out;
  }

  private periodEndFor(year: number, monthIndex0: number, conv: ProrateConvention): Date {
    if (conv === ProrateConvention.Monthly) {
      return new Date(year, monthIndex0 + 1, 0);
    }
    if (conv === ProrateConvention.Quarterly) {
      const q = Math.floor(monthIndex0 / 3);
      const endMonth = q * 3 + 2;
      return new Date(year, endMonth + 1, 0);
    }
    if (conv === ProrateConvention.BiAnnual) {
      const isH1 = monthIndex0 < 6;
      const endMonth = isH1 ? 5 : 11;
      return new Date(year, endMonth + 1, 0);
    }
    return new Date(year, 12, 0);
  }

  private filterToCompletedPeriods(keys: string[], asOf: Date, conv: ProrateConvention): string[] {
    const completed: string[] = [];
    for (const key of keys) {
      const [ys, ms] = key.split('-');
      const y = parseInt(ys, 10);
      const m0 = parseInt(ms, 10) - 1;
      const periodEnd = this.periodEndFor(y, m0, conv);
      if (periodEnd < new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate())) {
        completed.push(key);
      }
    }
    return completed;
  }

  private recomputeEligibility(): void {
    // Compute hasUnposted first
    this.computeHasUnpostedDep();
    // Then compute canPost
    if (!this.asset || !this.config) {
      this.canPostDepBool = false;
      return;
    }
    if (this.asset.depreciationMethod === DepreciationMethod.None) {
      this.canPostDepBool = false;
      return;
    }
    const expense = this.asset.depreciationExpenseAccountId || this.config.defaultDepreciationExpenseAccountId;
    const accum = this.asset.accumulatedDepreciationAccountId || this.config.defaultAccumulatedDepreciationAccountId;
    // If disposed, never allow dep posting
    const isDisposed = !!this.asset?.disposal?.disposalDate;
    this.canPostDepBool = !!(expense && accum) && this.hasUnpostedDepBool && !isDisposed;
  }

  // Only recompute after BOTH asset and config are ready; batches rapid calls into next microtask
  private recomputeWhenReady(): void {
    if (!this.assetReady || !this.configReady) {
      return;
    }
    // Microtask to coalesce multiple triggers in the same tick
    Promise.resolve().then(() => this.recomputeEligibility());
  }

  onPostCurrentDep(): void {
    if (!this.asset || !this.config) {
      return;
    }
    // Prevent depreciation posting if disposed
    const disposed = !!this.asset?.disposal?.disposalDate;
    if (disposed) {
      this.notification.showWarningToast($localize`:DisposedNoDep@@disposedNoDep:Asset has a disposal date. Depreciation cannot be posted.`);
      return;
    }
    const messages: string[] = [
      $localize`:ConfirmPost@@confirmPost:Post current unposted depreciation to GL?`,
      `${$localize`:Asset@@asset:Asset`}: ${this.form?.value?.name || this.asset?.name || this.asset?.id || ''}`,
    ];

    this.confirm.confirmThis(
      messages,
      () => {
        // Use batch posting API with a single asset for consistency
        this.posting
          .postDepreciationBatch([this.asset as FixedAssetModel], this.config as FixedAssetConfigModel)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            // refresh asset from service to update posted months and schedule/amounts if changed
            const id = this.asset?.id;
            if (id) {
              this.ds
                .getById(id)
                .pipe(takeUntil(this.destroy$))
                .subscribe(a => {
                  if (a) {
                    this.asset = a;
                    const {acquisitionDate, inServiceDate, ...rest} = a as any;
                    // Prevent preview triggers caused by refresh patch after posting
                    this.allowPreviewChanges = false;
                    this.form.patchValue({
                      ...rest,
                      acquisitionDate: this.toNgbDate(a.acquisitionDate),
                      inServiceDate: this.toNgbDate(a.inServiceDate || a.acquisitionDate)
                    }, {emitEvent: false});
                    // Recompute aggregates/eligibility after refresh
                    this.computeDepreciationToDate();
                    this.computeMacrsDepreciationToDate();
                    this.recomputeEligibility();
                    // Mark clean and re-enable preview for future user edits
                    this.form.markAsPristine();
                    this.allowPreviewChanges = true;
                  }
                });
            }
          });
      },
      () => {
        this.notification.showSuccessToast($localize`:Post Canceled@@postCanceled:Post Canceled`);
      }
    );
  }

  // Disposal posting
  get canEditDisposal(): boolean {
    // Only lock the disposal fields AFTER a disposal entry has been posted.
    // Backend returns the posting date in DisposalEntryDate (normalized to disposal.entryDate).
    // The picker-chosen DisposalDate should NOT lock the fields by itself.
    return !this.asset?.disposal?.entryDate;
  }

  get canPostDisposal(): boolean {
    if (!this.canEditDisposal) {
      return false;
    }
    const v = this.form?.value as any;
    return !!v?.disposalDate && (v?.disposalAmount != null && v?.disposalAmount !== '');
  }

  // Ensure reactive disabling is driven by FormControl state, not template [disabled]
  private updateDisposalControlState(): void {
    try {
      const editable = this.canEditDisposal;
      const names = ['disposalDate', 'disposalAmount', 'disposalExpenses'];
      for (const n of names) {
        const c = this.form.get(n);
        if (!c) {
          continue;
        }
        if (editable) {
          if (c.disabled) {
            c.enable({emitEvent: false});
          }
        } else {
          if (c.enabled) {
            c.disable({emitEvent: false});
          }
        }
      }
    } catch {
    }
  }

  onPostAssetDisposal(): void {
    if (!this.asset || !this.config) {
      return;
    }
    const v = this.form.value as any;
    const dateIso = this.toIsoDate(v.disposalDate);
    const amount = Number(v.disposalAmount);
    const expenses = v.disposalExpenses != null && v.disposalExpenses !== '' ? Number(v.disposalExpenses) : undefined;
    if (!dateIso || isNaN(amount)) {
      return;
    }

    const messages: string[] = [
      $localize`:ConfirmDisposal@@confirmDisposal:Post asset disposal to GL?`,
      `${$localize`:Asset@@asset:Asset`}: ${this.form?.value?.name || this.asset?.name || this.asset?.id || ''}`,
      `${$localize`:Disposal Date@@disposalDate:Disposal Date`}: ${dateIso}`,
      `${$localize`:Amount@@amount:Amount`}: ${amount.toFixed(2)}`,
      expenses != null ? `${$localize`:Expenses@@expenses:Expenses`}: ${Number(expenses).toFixed(2)}` : ''
    ].filter(Boolean) as string[];

    this.confirm.confirmThis(
      messages,
      () => {
        this.posting.postAssetDisposal(this.asset as FixedAssetModel, this.config as FixedAssetConfigModel, {
          date: dateIso,
          amount,
          expenses,
        }).pipe(takeUntil(this.destroy$)).subscribe(() => {
          // refresh asset
          const id = this.asset?.id;
          if (!id) {
            return;
          }
          this.ds.getById(id).pipe(takeUntil(this.destroy$)).subscribe(a => {
            if (!a) {
              return;
            }
            this.asset = a;
            const {acquisitionDate, inServiceDate, disposal, ...rest} = a as any;
            // Prevent preview triggers during refresh patch after disposal posting
            this.allowPreviewChanges = false;
            this.form.patchValue({
              ...rest,
              acquisitionDate: this.toNgbDate(a.acquisitionDate),
              inServiceDate: this.toNgbDate(a.inServiceDate || a.acquisitionDate),
              // After posting, show the DisposalDate in the picker; entryDate comes from backend and locks fields
              disposalDate: this.toNgbDate(disposal?.disposalDate) ?? null,
              disposalAmount: disposal?.proceeds ?? null,
              disposalExpenses: disposal?.expenses ?? null,
            }, {emitEvent: false});
            this.updateDisposalControlState();
            this.computeDepreciationToDate();
            this.computeMacrsDepreciationToDate();
            this.recomputeEligibility();
            // Mark clean and re-enable preview for future user edits
            this.form.markAsPristine();
            this.allowPreviewChanges = true;
          });
        });
      },
      () => {
        this.notification.showSuccessToast($localize`:Post Canceled@@postCanceled:Post Canceled`);
      }
    );
  }

  // Helpers: date parsing/formatting for form <-> model mapping
  private buildReportHtmlShell(title: string, inner: string): string {
    const runAt = new Date().toLocaleString();
    const styles = `
      <style>
        body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 16px; }
        h3 { margin: 0 0 8px 0; }
        .meta { color: #666; margin-bottom: 12px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ddd; padding: 6px; }
        th { background: #f7f7f7; text-align: left; }
        .text-end { text-align: right; }
      </style>`;
    return `<!DOCTYPE html><html><head><meta charset="utf-8"/>${styles}<title>${title}</title></head>
      <body>
        <h3>${title}</h3>
        <div class="meta">${$localize`:Generated@@generated:Generated`}: ${runAt}</div>
        ${inner || ''}
      </body></html>`;
  }
  private parseToDate(val?: string | Date | null): Date | null {
    if (!val) {
      return null;
    }
    if (val instanceof Date) {
      return isNaN(val.getTime()) ? null : val;
    }
    const s = String(val).trim();
    if (!s) {
      return null;
    }
    // Accept common formats; prefer ISO strings
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  ngbDateToDisplay(d?: NgbDateStruct | null): string {
    if (!d || !d.year || !d.month || !d.day) {
      return '';
    }
    return `${String(d.month).padStart(2, '0')}/${String(d.day).padStart(2, '0')}/${d.year}`;
  }

  private toNgbDate(val?: string | null): NgbDateStruct | null {
    if (!val) {
      return null;
    }
    const s = String(val).trim();
    if (!s) {
      return null;
    }
    // Parse as local date to avoid UTC offset shifting the day
    const parts = s.split('T')[0].split('-');
    if (parts.length === 3) {
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      const d = parseInt(parts[2], 10);
      if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
        return {year: y, month: m, day: d};
      }
    }
    return null;
  }

  private toIsoDate(d?: NgbDateStruct | Date | null): string | null {
    if (!d) {
      return null;
    }
    if (d instanceof Date) {
      if (isNaN(d.getTime())) {
        return null;
      }
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    // NgbDateStruct
    const ng = d as NgbDateStruct;
    if (!ng.year || !ng.month || !ng.day) {
      return null;
    }
    return `${ng.year}-${String(ng.month).padStart(2, '0')}-${String(ng.day).padStart(2, '0')}`;
  }

  private findNameById(list: any[] | undefined, id: string | undefined): string | undefined {
    if (!list || !id) {
      return undefined;
    }
    try {
      const item = (list as any[]).find(x => String(x?.id) === String(id));
      const name = item?.name ?? item?.Name ?? undefined;
      return name ? String(name) : undefined;
    } catch {
      return undefined;
    }
  }

  // Account modal handlers (shared with config form pattern)
  beginAccountPick(formControlName: keyof FixedAssetModel): void {
    this.vm.tempAccountDetailId = formControlName as string;
  }

  onAccountSelected(accountId: string): void {
    const ctrlName = this.vm.tempAccountDetailId as string;
    if (!ctrlName) {
      return;
    }
    this.form.get(ctrlName)?.setValue(accountId);
  }

  refreshAccounts(): void {
    this.tableDataService
      .getApiListData('account', '?state=active&pageNumber=0&pageSize=999999')
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: ResponseModel) => {
        const accounts = (result as any)?.Data ?? result;
        this.vm.account = accounts;
        this.accountLookup = new Map(
          (accounts as any[]).map((a: any) => [String(a.id), {id: String(a.id), number: a.number, name: a.name}])
        );
        // ensure lookup is considered ready after manual refresh too
        this.accountsLoaded = true;
      });
  }

  renderAccount(id: string | null | undefined): string {
    if (!id) {
      return '';
    }
    // if accounts not yet loaded, suppress raw id to avoid flicker
    if (!this.accountsLoaded) {
      return '';
    }
    const a = this.accountLookup.get(String(id));
    return a ? `${a.number} • ${a.name}` : '';
  }

  // Load the asset for a given id or clear for 'new'
  private loadAsset(id: string | null) {
    // reset transient state before each load
    this.assetReady = false;
    this.asset = undefined as any;
    // Prevent preview calls while we patch the incoming model
    this.allowPreviewChanges = false;

    if (!id || id === 'new') {
      // Clear the form to defaults for new asset
      const defaults = {
        id: '',
        name: '',
        assetTag: '',
        category: '',
        description: '',
        acquisitionDate: null,
        inServiceDate: null,
        acquisitionCost: 0,
        salvageValue: 0,
        usefulLifeMonths: 60,
        depreciationMethod: DepreciationMethod.None,
        totalUnits: 0,
        companyId: '',
        locationId: '',
        entityId: '',
        assetAccountId: '',
        accumulatedDepreciationAccountId: '',
        depreciationExpenseAccountId: '',
        gainOnDisposalAccountId: '',
        lossOnDisposalAccountId: '',
        proceedsSuspenseAccountId: '',
        active: true,
        disposalDate: null,
        disposalAmount: null,
        disposalExpenses: null,
      } as any;
      this.form.reset(defaults);
      // allow account defaults to be applied once for this new asset
      this.didApplyAccountDefaults = false;
      // If config is already loaded, apply defaults now
      this.applyAccountDefaultsIfNew();
      // Ensure disposal control state matches editable status (new assets are editable)
      this.updateDisposalControlState();
      this.assetReady = true;
      this.recomputeWhenReady();
      // For new assets, no schedule yet; ensure dep-to-date is zero
      this.depToDateToday = 0;
      // Allow preview for new asset once fields become complete
      this.allowPreviewChanges = true;
      // Return a noop observable to fit switchMap signature
      return of(void 0);
    }

    // Fetch and load existing asset
    return this.ds.getById(id).pipe(
      takeUntil(this.destroy$),
      tap(a => {
        if (a) {
          // Normalize date fields defensively (API may send null/empty)
          const safeAsset = {
            ...a,
            acquisitionDate: a.acquisitionDate || null,
            inServiceDate: a.inServiceDate || null,
          } as FixedAssetModel;
          this.asset = safeAsset;
          // Patch non-date fields directly
          const {acquisitionDate, inServiceDate, disposal, ...rest} = safeAsset as any;
          this.form.patchValue({
            ...rest,
            acquisitionDate: this.toNgbDate(safeAsset.acquisitionDate),
            inServiceDate: this.toNgbDate(safeAsset.inServiceDate || safeAsset.acquisitionDate),
            // Populate disposal controls from API: picker uses DisposalDate, amounts from proceeds/expenses
            disposalDate: this.toNgbDate(disposal?.disposalDate) ?? null,
            disposalAmount: disposal?.proceeds ?? null,
            disposalExpenses: disposal?.expenses ?? null,
          });
          // Align control disabled state with disposal status
          this.updateDisposalControlState();
          // Align useful life with MACRS category if applicable
          const m = this.macrsOptions.find(o => o.value === this.form.value.category);
          if (m && this.form.value.usefulLifeMonths !== m.months) {
            this.form.get('usefulLifeMonths')?.setValue(m.months);
          }
          // No local MACRS maps; rely on API values in schedule rows.
          // Per requirement: Do NOT call preview when loading the form.
          // If API returns no schedule, simply display none until the user edits fields (then preview may be triggered).
          // mark asset as ready for UI gating
          this.assetReady = true;
          this.recomputeWhenReady();
          // Compute Depreciation to Date based on today's date and current schedule
          try {
            this.computeDepreciationToDate();
            this.computeMacrsDepreciationToDate();
          } catch {
          }
          // Enable preview on subsequent user edits only after initial load has completed.
          // Defer to next macrotask to avoid racing with late valueChanges emissions from patchValue.
          setTimeout(() => {
            this.allowPreviewChanges = true;
          }, 0);
        } else {
          try {
            console.warn('[FA Form] getById returned no asset for id', id);
          } catch {
          }
        }
      }),
      map(() => void 0)
    );
  }

  // Local fallback schedule generation removed: API is the single source of truth

  // --- Preview schedule integration ---
  private setupPreviewScheduleSubscriptions(): void {
    // Trigger preview when ANY relevant field changes (after blur for inputs using updateOn:'blur').
    // Use per-control streams with startWith(current value) so we aren't blocked by combineLatest
    // waiting for every control to emit at least once.
    const names: string[] = [
      'acquisitionDate',
      'inServiceDate',
      'acquisitionCost',
      'salvageValue',
      'usefulLifeMonths',
      // Method is handled by a dedicated pairwise watcher to avoid duplicates
      'category',
      'totalUnits',
      // Optional field; may not exist on the form
      'unitsPerMonthOverride'
    ];
    const perControl$ = names
      .map(n => this.form.get(n))
      .filter((c): c is UntypedFormControl => !!c)
      .map(c => c.valueChanges.pipe(startWith(c.value), distinctUntilChanged()));

    // If no controls found (unlikely), do nothing
    if (!perControl$.length) {
      return;
    }

    // Merge so any single control change triggers the preview once, debounced
    merge(...perControl$)
      .pipe(takeUntil(this.destroy$), debounceTime(250))
      .subscribe(() => {
        // Skip any emissions caused by initial form patching during load
        if (!this.allowPreviewChanges) {
          return;
        }
        // Let Angular finish applying the last change to the form
        setTimeout(() => this.requestPreviewSchedule(), 0);
      });
  }

  private requestPreviewSchedule(force: boolean = false): void {
    try {
      if (!this.assetReady) {
        return;
      }

      // Block previews during initial load/patch for existing assets
      // Only allow after the user actually edits (form becomes dirty)
      if (!force && this.form?.pristine) {
        return;
      }

      // Do not preview when method is None
      const currMethod = String(this.form?.value?.depreciationMethod || '').trim();
      if (currMethod === 'None') {
        return;
      }

      // Build the latest draft after current microtask to avoid race on valueChanges
      const draft = this.buildPreviewPayloadFromForm();
      if (!draft) {
        return;
      }

      // Optional extra protection (keeps original intent):
      // If we already have an API schedule and we’re still in the very first frame of enablement, skip
      if (!force && !this.allowPreviewChanges && Array.isArray(this.asset?.schedule) && this.asset.schedule.length > 0) {
        return;
      }

      try {
        console.log('[FA Form] preview-schedule intent payload', draft);
      } catch {
      }
      this.ds.previewSchedule(draft)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          try {
            console.log('[FA Form] preview-schedule response', res);
          } catch {
          }
          // Preview API standard: ScheduleEntries; keep defensive fallback to Rows if ever returned
          const entries = Array.isArray(res?.ScheduleEntries) ? res.ScheduleEntries : (Array.isArray(res?.Rows) ? res.Rows : []);
          if (!entries.length) {
            return;
          }
          const sched = entries.map((e: any, idx: number) => ({
            periodStart: e.PeriodStart ?? e.periodStart,
            periodEnd: e.PeriodEnd ?? e.periodEnd,
            periodNumber: Number(e.PeriodNumber ?? e.periodNumber ?? (idx + 1)),
            // Strictly use server-provided Book fields
            depreciationAmount: Number(e.BookDepreciation ?? 0),
            accumulatedDepreciation: Number(e.BookAccumulated ?? 0),
            endingBookValue: Number(e.EndingNBV ?? 0),
            macrsDepreciation: e.MacrsDepreciation ?? e.macrsDepreciation ?? null,
            macrsAccumulated: e.MacrsAccumulated ?? e.macrsAccumulated ?? null,
          }));
          this.asset = {...(this.asset || {} as any), schedule: sched} as FixedAssetModel;
          // Recalculate simple aggregates from the new schedule
          this.computeDepreciationToDate();
          this.computeMacrsDepreciationToDate();
        });
    } catch {
    }
  }

  private buildPreviewPayloadFromForm(): any | null {
    const v: any = this.form?.value || {};
    // Strong readiness validation
    const acqIso = this.toIsoDate(v.acquisitionDate);
    const insIso = this.toIsoDate(v.inServiceDate || v.acquisitionDate);
    const acq = acqIso ? new Date(acqIso + 'T00:00:00') : null;
    const ins = insIso ? new Date(insIso + 'T00:00:00') : null;
    const cost = Number(v.acquisitionCost);
    const salvage = Number(v.salvageValue ?? 0);
    const life = Number(v.usefulLifeMonths);
    const method = String(v.depreciationMethod || '').trim();
    if (!acq || !ins || !isFinite(cost) || cost <= 0 || !isFinite(life) || life < 1 || !method || method === 'None') {
      return null;
    }

    // Build DTO, omitting non-applicable or null fields
    const dto: any = {
      Id: String(v.id || this.asset?.id || '00000000-0000-0000-0000-000000000000'),
      Name: String(v.name || this.asset?.name || ''),
      // Always include AssetTag and Category keys to match required preview shape
      AssetTag: String(v.assetTag || this.asset?.assetTag || ''),
      Category: String(v.category || this.asset?.category || ''),
      AcquisitionDate: this.toIsoDateTimeMidnight(acq)!,
      InServiceDate: this.toIsoDateTimeMidnight(ins)!,
      AcquisitionCost: cost,
      SalvageValue: isFinite(salvage) ? salvage : 0,
      UsefulLifeMonths: life,
      DepreciationMethod: method,
    };
    if (method === 'UnitsOfProduction') {
      const totalUnits = Number(v.totalUnits);
      if (!isFinite(totalUnits) || totalUnits <= 0) {
        return null; // require total units for UoP
      }
      dto.TotalUnits = totalUnits;
      if (v.unitsPerMonthOverride != null && v.unitsPerMonthOverride !== '') {
        const upmo = Number(v.unitsPerMonthOverride);
        if (isFinite(upmo) && upmo > 0) {
          dto.UnitsPerMonthOverride = upmo;
        }
      }
    }
    return dto;
  }

  private toIsoDateTimeMidnight(d: Date | null): string | null {
    if (!d || isNaN(d.getTime())) {
      return null;
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}T00:00:00`;
  }

  // Sum of schedule depreciation amounts through today (by periodEnd)
  private computeDepreciationToDate(): void {
    try {
      const sched = this.asset?.schedule || [];
      const today = new Date();
      let sum = 0;
      for (const e of sched) {
        const pe = e && (e as any).periodEnd ? new Date((e as any).periodEnd) : undefined;
        if (pe && pe <= today) {
          sum += Number((e as any).depreciationAmount) || 0;
        }
      }
      this.depToDateToday = +sum.toFixed(2);
    } catch {
      this.depToDateToday = 0;
    }
  }

  // Sum of MACRS depreciation amounts through today (by periodEnd), using macrsMap keyed by YYYY-MM of periodStart
  private computeMacrsDepreciationToDate(): void {
    try {
      const sched = this.asset?.schedule || [];
      const today = new Date();
      let sum = 0;
      for (const e of sched) {
        const pe = e && (e as any).periodEnd ? new Date((e as any).periodEnd) : undefined;
        if (!pe || pe > today) {
          continue;
        }
        const macrs = (e as any).macrsDepreciation;
        if (macrs !== null && macrs !== undefined) {
          sum += Number(macrs) || 0;
        }
      }
      this.macrsDepToDateToday = +sum.toFixed(2);
    } catch {
      this.macrsDepToDateToday = 0;
    }
  }

  // --- UI helpers for Tag example and default accounts ---
  private applyAccountDefaultsIfNew(): void {
    try {
      if (this.didApplyAccountDefaults) {
        return;
      }
      const isNew = !this.form?.value?.id;
      if (!isNew) {
        return;
      }
      if (!this.configReady || !this.config) {
        return;
      }
      const ctrlNames: Array<[keyof FixedAssetModel, keyof FixedAssetConfigModel | undefined]> = [
        ['assetAccountId', 'defaultAssetAccountId'],
        ['accumulatedDepreciationAccountId', 'defaultAccumulatedDepreciationAccountId'],
        ['depreciationExpenseAccountId', 'defaultDepreciationExpenseAccountId'],
        ['gainOnDisposalAccountId', 'defaultGainOnDisposalAccountId'],
        ['lossOnDisposalAccountId', 'defaultLossOnDisposalAccountId'],
        // Proceeds suspense: support both config casings
        ['proceedsSuspenseAccountId', 'defaultProceedsSuspenseAccountId'],
      ];
      let applied = false;
      for (const [formCtrl, cfgKey] of ctrlNames) {
        const ctrl = this.form.get(formCtrl as string);
        const curr = ctrl?.value;
        const defVal = cfgKey ? (this.config as any)[cfgKey] : undefined;
        if ((curr == null || curr === '') && defVal) {
          ctrl?.setValue(defVal);
          applied = true;
        }
      }
      if (applied) {
        this.didApplyAccountDefaults = true;
        // Updating these can change eligibility
        this.recomputeWhenReady();
      }
    } catch {
    }
  }

  // Build an example tag based on config.assetTagPattern; used only on new asset when tag is empty
  get assetTagExample(): string {
    try {
      const pattern = (this.config as any)?.assetTagPattern || '';
      if (!pattern) {
        return '';
      }
      const now = new Date();
      const yyyy = String(now.getFullYear());
      const yy = yyyy.slice(-2);
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      let out = String(pattern);
      out = out.replaceAll('{YYYY}', yyyy)
        .replaceAll('{YY}', yy)
        .replaceAll('{MM}', mm)
        .replaceAll('{DD}', dd);
      // Replace zero-padded counters like {0001}, {00001}
      out = out.replace(/\{0+1\}/g, (m: string) => {
        const len = m.length - 2; // remove braces
        // m looks like {0001} => len includes braces; find zero run length
        const zeros = m.slice(1, -1); // 0001
        const width = zeros.length;
        return String(1).padStart(width, '0');
      });
      // Replace generic {#} or {SEQ} patterns if present
      out = out.replace('{#}', '0001').replace('{SEQ}', '0001');
      return out;
    } catch {
      return '';
    }
  }
}
